import { Injectable } from '@angular/core';
import { CityActions } from '@city/data-access';
import { CategoriesMenuItem, OccMenuItem, OccMenuParams } from '@main/categories-menu/const';
import { CategoriesMenuActions, CategoriesMenuApiService, OccMenuService } from '@main/categories-menu/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, mergeMap, switchMap } from 'rxjs/operators';

function mapNode({ uid, entries, children, hasChildren }: OccMenuItem): CategoriesMenuItem {
  const { linkName, url, categoryCode, media } = entries[0];

  return {
    url,
    media,
    nodeId: uid,
    name: linkName,
    code: categoryCode,
    children: children?.map(mapNode),
    hasChildren,
  };
}

@Injectable()
export class CategoriesMenuEffects {
  loadCategoriesMenu$: Observable<
    CategoriesMenuActions.CategoriesMenuLoadSuccess | CategoriesMenuActions.CategoriesMenuLoadFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesMenuActions.LOAD_CATEGORIES_MENU),
      map((action: CategoriesMenuActions.CategoriesMenuLoad) => action.payload),
      mergeMap(({ nodeId, level }: OccMenuParams) => {
        return this.occMenuService.load({ nodeId, level }).pipe(
          map((res: OccMenuItem) => {
            return new CategoriesMenuActions.CategoriesMenuLoadSuccess({
              currentNodeId: nodeId,
              depth: level,
              entry: res.entries?.[0],
              items: res.children?.map(mapNode),
            });
          }),
          catchError((error: unknown) => of(new CategoriesMenuActions.CategoriesMenuLoadFail(error))),
        );
      }),
    ),
  );

  clearMenuStateOnChangeBaseStore$: Observable<CategoriesMenuActions.CategoriesMenuClearState> = createEffect(() =>
    this.actions$.pipe(
      ofType(CityActions.SET_ACTIVE_BASE_STORE),
      map(({ payload }) => payload),
      distinctUntilChanged(),
      map(() => new CategoriesMenuActions.CategoriesMenuClearState()),
    ),
  );

  loadRootNodeId$: Observable<
    CategoriesMenuActions.CategoriesMenuRootNodeIdLoadSuccess | CategoriesMenuActions.CategoriesMenuRootNodeIdLoadFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesMenuActions.LOAD_CATEGORIES_ROOT_NODE_ID_MENU),
      switchMap(() => {
        return this.categoriesMenuApiService.getRootNodeId().pipe(
          map((res: string) => {
            return new CategoriesMenuActions.CategoriesMenuRootNodeIdLoadSuccess(res);
          }),
          catchError((error: unknown) => of(new CategoriesMenuActions.CategoriesMenuRootNodeIdLoadFail(error))),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private occMenuService: OccMenuService,
    private categoriesMenuApiService: CategoriesMenuApiService,
  ) {}
}
